import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexto/AuthContexto';
import api from '@/lib/api';
import { getWhatsAppLink } from '@/lib/whatsapp';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ShieldOff, CreditCard, Landmark, ArrowRight, LogOut, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function SuscripcionBloqueada() {
  const { usuario, suscripcion, cerrarSesion } = useAuth();
  const navigate = useNavigate();
  const [cargando, setCargando] = useState(false);
  const [cargandoStripe, setCargandoStripe] = useState(false);
  const [whatsapp, setWhatsapp] = useState('529995538880');
  const [precio, setPrecio] = useState(399);
  const [banco, setBanco] = useState({ clabe: '', banco: '' });
  const [pasarela, setPasarela] = useState(null);

  useEffect(() => {
    api.get('/platform-whatsapp').then(r => setWhatsapp(r.data.whatsapp)).catch(() => {});
    api.get('/pagos/mi-suscripcion').then(r => setPrecio(r.data.precio || 399)).catch(() => {});
    api.get('/admin/datos-bancarios').then(r => setBanco(r.data)).catch(() => {});
    api.get('/pagos/pasarela-pago').then(r => setPasarela(r.data)).catch(() => {});
  }, []);

  const iniciarPagoMP = async () => {
    setCargando(true);
    try {
      const origin = window.location.origin;
      const res = await api.post('/pagos/crear-suscripcion', { origin_url: origin });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error('No se recibio enlace de pago');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Error desconocido';
      toast.error(`Error al iniciar el pago: ${msg}`);
    }
    setCargando(false);
  };

  const iniciarPagoStripe = async () => {
    setCargandoStripe(true);
    try {
      const origin = window.location.origin;
      const res = await api.post('/pagos/stripe/crear-checkout', { origin_url: origin });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error('No se recibio enlace de pago de Stripe');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Error desconocido';
      toast.error(`Error al iniciar Stripe: ${msg}`);
    }
    setCargandoStripe(false);
  };

  const whatsappComprobanteUrl = () => {
    const msg = `Hola! Acabo de realizar una transferencia por $${precio} MXN para reactivar mi suscripcion de Talleres PRO.\n\nMi correo: ${usuario?.email || ''}\nNegocio: ${usuario?.tenant_nombre || ''}\n\nAdjunto mi comprobante de pago.`;
    return getWhatsAppLink(whatsapp, msg);
  };

  const esMexico = !pasarela || pasarela.pais === 'MX';

  const handleLogout = () => {
    cerrarSesion();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4" data-testid="suscripcion-bloqueada-page">
      <div className="max-w-lg w-full text-center">
        <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
          <ShieldOff className="w-10 h-10 text-red-400" />
        </div>

        <h1 className="font-barlow text-3xl font-bold text-slate-50 uppercase mb-3">
          Cuenta Suspendida
        </h1>
        <p className="text-slate-400 mb-4 text-base">
          Tu cuenta ha sido suspendida. Para reactivar tu acceso, realiza el pago de tu suscripcion.
        </p>

        <div className="bg-slate-800/50 border border-slate-700 rounded-sm p-4 mb-6">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Informacion de tu cuenta</p>
          <div className="space-y-1 text-sm text-left">
            <div className="flex justify-between">
              <span className="text-slate-500">Negocio:</span>
              <span className="text-slate-300">{usuario?.tenant_nombre}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Estado:</span>
              <span className="text-red-400">Suspendido</span>
            </div>
            {suscripcion?.vence && (
              <div className="flex justify-between">
                <span className="text-slate-500">Ultimo vencimiento:</span>
                <span className="text-slate-300">{new Date(suscripcion.vence).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-3 mb-6">
          <p className="text-sm text-amber-300">
            Reactiva tu acceso eligiendo tu metodo de pago preferido.
          </p>
        </div>

        <div className="space-y-4 mb-8">
          {/* Opcion 1: Stripe */}
          <Card className="bg-slate-900/60 border-indigo-700/50 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-indigo-500" />
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-indigo-500/10 flex items-center justify-center shrink-0">
                  <CreditCard className="w-6 h-6 text-indigo-400" />
                </div>
                <div className="text-left flex-1">
                  <h3 className="font-barlow font-bold text-slate-100">Opcion 1 — Stripe</h3>
                  <p className="text-sm text-slate-400">Tarjeta internacional - Visa, Mastercard, Amex</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-indigo-400 font-barlow">{pasarela?.precio ? `$${pasarela.precio}` : `$${precio}`}</p>
                  <p className="text-xs text-slate-500">{pasarela?.moneda || 'MXN'} / mes</p>
                </div>
              </div>
              <Button onClick={iniciarPagoStripe} disabled={cargandoStripe}
                className="w-full mt-4 bg-indigo-500 hover:bg-indigo-600 text-white font-bold rounded-sm h-11"
                data-testid="pagar-stripe-suspendido-btn">
                {cargandoStripe ? 'Procesando...' : 'Reactivar con Stripe'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Opcion 2: Mercado Pago (solo Mexico) */}
          {esMexico && (
          <Card className="bg-slate-900/60 border-amber-700/50 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500" />
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-amber-500/10 flex items-center justify-center shrink-0">
                  <CreditCard className="w-6 h-6 text-amber-500" />
                </div>
                <div className="text-left flex-1">
                  <h3 className="font-barlow font-bold text-slate-100">Opcion 2 — Mercado Pago</h3>
                  <p className="text-sm text-slate-400">Cobro automatico mensual - tarjeta, OXXO o SPEI</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-amber-500 font-barlow">${precio}</p>
                  <p className="text-xs text-slate-500">MXN / mes</p>
                </div>
              </div>
              <Button onClick={iniciarPagoMP} disabled={cargando}
                className="w-full mt-4 bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm h-11"
                data-testid="pagar-mp-suspendido-btn">
                {cargando ? 'Procesando...' : 'Reactivar con Mercado Pago'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
          )}

          {/* Opcion 3: Deposito Bancario (solo Mexico) */}
          {esMexico && (
          <Card className="bg-slate-900/60 border-slate-700">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <Landmark className="w-6 h-6 text-emerald-400" />
                </div>
                <div className="text-left flex-1">
                  <h3 className="font-barlow font-bold text-slate-100">Opcion 3 — Deposito Bancario</h3>
                  <p className="text-sm text-slate-400">Transfiere y envia tu comprobante</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-emerald-400 font-barlow">${precio}</p>
                  <p className="text-xs text-slate-500">MXN / mes</p>
                </div>
              </div>
              <div className="mt-4 bg-slate-800/50 rounded-sm p-3 text-left">
                <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2">Datos bancarios:</p>
                <div className="space-y-1 text-sm text-slate-300">
                  <p>Banco: <span className="text-slate-100 font-medium">{banco.banco || 'BBVA'}</span></p>
                  <p>CLABE: <span className="text-slate-100 font-medium font-mono">{banco.clabe || '---'}</span></p>
                  <p>Referencia: <span className="text-amber-400 font-medium">{usuario?.email || 'tu correo'}</span></p>
                </div>
                <p className="text-xs text-slate-500 mt-2">Activacion en menos de 24 horas.</p>
              </div>
              <a href={whatsappComprobanteUrl()} target="_blank" rel="noopener noreferrer"
                onClick={(e) => { e.preventDefault(); const w = window.open(whatsappComprobanteUrl(), '_blank'); if (!w || w.closed) window.location.href = whatsappComprobanteUrl(); }}
                className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-sm h-10 flex items-center justify-center gap-2 transition-colors cursor-pointer"
                data-testid="enviar-comprobante-whatsapp-suspendido-btn">
                <MessageCircle className="w-4 h-4" /> Enviar Comprobante por WhatsApp
              </a>
            </CardContent>
          </Card>
          )}
        </div>

        <p className="text-xs text-slate-600 mb-4">
          Tus datos estan seguros. Una vez renovada la suscripcion, recuperaras acceso completo a tu informacion.
        </p>

        <Button variant="ghost" onClick={handleLogout} className="text-slate-500 hover:text-slate-300" data-testid="cerrar-sesion-bloqueado-btn">
          <LogOut className="w-4 h-4 mr-2" /> Cerrar Sesion
        </Button>
      </div>
    </div>
  );
}
